import * as THREE from "three";

/**
 * Grok fun-first arcade-idle juice: confetti, coins, ambient sparkles, camera punch.
 * Presentation-only. Uses instanced meshes so draw submissions stay within D-030 budgets.
 */

export interface JuiceBurstRequest {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly kind: "harvest" | "reward" | "sale" | "hire" | "craft" | "unlock" | "ambient";
  readonly intensity?: number;
  readonly bornAtSeconds: number;
}

export interface ArcadeIdleJuiceController {
  readonly root: THREE.Group;
  burst(request: Readonly<JuiceBurstRequest>): void;
  punch(strength?: number): void;
  update(elapsedSeconds: number, reducedMotion: boolean): void;
  applyCameraPunch(camera: THREE.PerspectiveCamera, deltaSeconds: number): void;
  dispose(): void;
}

interface Slot {
  bornAt: number;
  life: number;
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  spin: number;
  scale: number;
  color: number;
  active: boolean;
}

const MAX_SLOTS = 48;
const COLORS = Object.freeze({
  harvest: [0x7ecf4a, 0xc6e86a, 0xf2d04b] as const,
  reward: [0xffc857, 0xffe08a, 0xff9f1c] as const,
  sale: [0x3ddc84, 0xffe066, 0xffffff] as const,
  hire: [0x5b8def, 0x9ec1ff, 0xffe066] as const,
  craft: [0xd97bff, 0xff9ecd, 0xffe066] as const,
  unlock: [0xfff1a8, 0xffffff, 0x7ed6ff] as const,
  ambient: [0xffffff, 0xfff6c8, 0xc8f0ff] as const,
});

export function createArcadeIdleJuice(): ArcadeIdleJuiceController {
  const root = new THREE.Group();
  root.name = "grok-arcade-idle-juice";

  const geometry = new THREE.OctahedronGeometry(0.06, 0);
  const material = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 0.95,
    depthWrite: false,
    vertexColors: true,
  });
  // One instanced submission for all juice particles (budget-safe).
  const mesh = new THREE.InstancedMesh(geometry, material, MAX_SLOTS);
  mesh.name = "grok-juice-instances";
  mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  mesh.frustumCulled = false;
  mesh.count = 0;
  mesh.renderOrder = 8;
  if (mesh.instanceColor === null) {
    mesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(MAX_SLOTS * 3), 3);
  }
  root.add(mesh);

  const slots: Slot[] = Array.from({ length: MAX_SLOTS }, () => ({
    bornAt: Number.NEGATIVE_INFINITY,
    life: 0.6,
    x: 0,
    y: 0,
    z: 0,
    vx: 0,
    vy: 0,
    vz: 0,
    spin: 0,
    scale: 1,
    color: 0xffffff,
    active: false,
  }));

  let nextSlot = 0;
  let punchRemaining = 0;
  let punchStrength = 0;
  let ambientAccumulator = 0;
  let disposed = false;
  const matrix = new THREE.Matrix4();
  const position = new THREE.Vector3();
  const quaternion = new THREE.Quaternion();
  const scale = new THREE.Vector3();
  const color = new THREE.Color();
  const hidden = new THREE.Matrix4().makeScale(0, 0, 0);

  const spawnOne = (
    x: number,
    y: number,
    z: number,
    kind: JuiceBurstRequest["kind"],
    bornAt: number,
    intensity: number,
  ): void => {
    const slot = slots[nextSlot]!;
    nextSlot = (nextSlot + 1) % MAX_SLOTS;
    const palette = COLORS[kind];
    const angle = Math.random() * Math.PI * 2;
    const speed = (0.55 + Math.random() * 1.35) * intensity;
    slot.active = true;
    slot.bornAt = bornAt;
    slot.life = kind === "ambient" ? 1.2 + Math.random() * 0.6 : 0.5 + Math.random() * 0.45;
    slot.x = x;
    slot.y = y;
    slot.z = z;
    slot.vx = Math.cos(angle) * speed;
    slot.vy = (0.9 + Math.random() * 1.8) * intensity;
    slot.vz = Math.sin(angle) * speed;
    slot.spin = (Math.random() - 0.5) * 10;
    slot.scale = 0.7 + Math.random() * 0.7 * intensity;
    slot.color = palette[Math.floor(Math.random() * palette.length)]!;
  };

  const controller: ArcadeIdleJuiceController = {
    root,
    burst(request): void {
      if (disposed) return;
      const intensity = Math.max(0.35, Math.min(2.4, request.intensity ?? 1));
      const count = request.kind === "ambient"
        ? 2
        : request.kind === "sale" || request.kind === "unlock" || request.kind === "craft"
          ? Math.round(12 * intensity)
          : Math.round(8 * intensity);
      for (let i = 0; i < count; i += 1) {
        spawnOne(
          request.x + (Math.random() - 0.5) * 0.18,
          request.y + Math.random() * 0.12,
          request.z + (Math.random() - 0.5) * 0.18,
          request.kind,
          request.bornAtSeconds,
          intensity,
        );
      }
    },
    punch(strength = 1): void {
      if (disposed) return;
      punchStrength = Math.max(punchStrength, Math.min(2.5, strength));
      punchRemaining = 0.28;
    },
    update(elapsedSeconds, reducedMotion): void {
      if (disposed) return;
      ambientAccumulator += 0.016;
      if (!reducedMotion && ambientAccumulator > 0.7) {
        ambientAccumulator = 0;
        spawnOne(
          (Math.random() - 0.5) * 5,
          0.7 + Math.random() * 1.2,
          (Math.random() - 0.5) * 5,
          "ambient",
          elapsedSeconds,
          0.4,
        );
      }

      let live = 0;
      for (let i = 0; i < MAX_SLOTS; i += 1) {
        const slot = slots[i]!;
        if (!slot.active) {
          mesh.setMatrixAt(i, hidden);
          continue;
        }
        const age = elapsedSeconds - slot.bornAt;
        if (age < 0 || age > slot.life) {
          slot.active = false;
          mesh.setMatrixAt(i, hidden);
          continue;
        }
        const t = age / slot.life;
        if (!reducedMotion) {
          slot.x += slot.vx * 0.016;
          slot.y += slot.vy * 0.016;
          slot.z += slot.vz * 0.016;
          slot.vy -= 2.8 * 0.016;
        }
        const fade = 1 - t;
        const s = Math.max(0.01, slot.scale * (0.85 + Math.sin(t * Math.PI) * 0.35) * fade);
        position.set(slot.x, slot.y, slot.z);
        quaternion.setFromEuler(new THREE.Euler(t * slot.spin, t * slot.spin * 0.7, t * 2));
        scale.setScalar(s);
        matrix.compose(position, quaternion, scale);
        mesh.setMatrixAt(i, matrix);
        color.setHex(slot.color);
        mesh.setColorAt(i, color);
        live += 1;
      }
      mesh.count = MAX_SLOTS;
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor !== null) mesh.instanceColor.needsUpdate = true;
      mesh.visible = live > 0 || punchRemaining > 0;

      if (punchRemaining > 0) {
        punchRemaining = Math.max(0, punchRemaining - 0.016);
        if (punchRemaining === 0) punchStrength = 0;
      }
    },
    applyCameraPunch(camera, deltaSeconds): void {
      if (disposed || punchRemaining <= 0 || punchStrength <= 0) return;
      const t = punchRemaining / 0.28;
      const amp = punchStrength * 0.09 * t;
      const wobble = Math.sin((1 - t) * Math.PI * 6);
      camera.position.x += wobble * amp * 0.35;
      camera.position.y += Math.abs(wobble) * amp * 0.25;
      camera.position.z += wobble * amp * 0.2;
      punchStrength = Math.max(0, punchStrength - deltaSeconds * 3.5);
    },
    dispose(): void {
      if (disposed) return;
      disposed = true;
      geometry.dispose();
      material.dispose();
      root.clear();
    },
  };

  return controller;
}
